import type { OrderDetail } from "../../services/use-orders"
import { formatDateTime } from "../../lib/format-date"

export function InvestigationNote({
  investigation,
}: {
  investigation: NonNullable<OrderDetail["investigation"]>
}) {
  return (
    <section className="py-4" aria-label="Nova's investigation">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Nova’s investigation</h3>

        <span className="text-xs text-muted-foreground">
          {formatDateTime(investigation.investigatedAt)}
        </span>
      </div>

      {investigation.stale ? (
        <p className="mt-2 text-sm text-warning">
          Outdated: the problems changed after this was written.
        </p>
      ) : null}

      {investigation.summary ? <InvestigationSummary text={investigation.summary} /> : null}
    </section>
  )
}

/** The recorded summary is Slack-flavoured plain text: newline-separated
 *  sentences with `backtick` code spans (ids, statuses, address values).
 *  Honor those two affordances — one line per sentence, code chips for the
 *  backtick spans — instead of collapsing everything into a single raw
 *  paragraph. Anything else stays literal text. */
function InvestigationSummary({ text }: { text: string }) {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
  return (
    <div className="mt-1 space-y-1">
      {lines.map((line, lineIndex) => (
        <p key={lineIndex} className="break-words text-sm leading-relaxed text-muted-foreground">
          {line.split(/(`[^`]+`)/).map((part, partIndex) =>
            part.startsWith("`") && part.endsWith("`") && part.length > 2 ? (
              <code
                key={partIndex}
                className="rounded bg-muted px-1 font-mono text-[11px] text-foreground"
              >
                {part.slice(1, -1)}
              </code>
            ) : (
              part
            )
          )}
        </p>
      ))}
    </div>
  )
}
